import { ReportRequest } from '@/client/reports/types/reports.type';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin/ChartEmptyState';
import { useCompletionsOverTimeReports } from '@/hooks/query/dashboard.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AccordionTabHeader } from '@/ui/accordion';
import { CompletionsLineChart } from '@/ui/charts/line-chart';
import { FlexContainer } from '@/ui/styled-ui';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const ChartContainer = styled(FlexContainer)`
  width: 100%;
  height: 510px;
  margin-top: 0;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: var(--white-main);
`;

type CompletionsOverTimeProps = {
  filters: ReportRequest;
};

export const CompletionsOverTime = ({ filters }: CompletionsOverTimeProps) => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { completionsOverTime, isLoading } = useCompletionsOverTimeReports(
    currentAccount?.id as string,
    filters,
  );

  const result = completionsOverTime?.result ?? [];
  const hasNoCompletions =
    result.length === 0 ||
    result.every((r) => Number(r.cumulativeCompleted) === 0);

  return (
    <ChartContainer id="completions-over-time-container">
      <AccordionTabHeader
        title={t('dashboard.awareness.completions.title')}
        description={t('dashboard.awareness.completions.description')}
        isSubHeader
      />
      {isLoading ? (
        <Skeleton height="100%" width="100%" />
      ) : hasNoCompletions ? (
        <ChartEmptyState
          minHeight={375}
          noMargin
          noBorder
          type={ChartEmptyStateType.AWT}
          title={t('dashboard.awareness.completions.empty.title')}
          description={t('dashboard.awareness.completions.empty.description')}
        />
      ) : (
        <CompletionsLineChart
          id="completions-over-time"
          fromDate={filters.from}
          toDate={filters.to}
          reportData={completionsOverTime?.result}
          isLoading={isLoading}
        />
      )}
    </ChartContainer>
  );
};
